import React from "react";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import './cssMain.css';
import ChartWrapper from "./ChartWrapper.jsx";

dayjs.extend(utc);
dayjs.extend(timezone);

const BusyTimesView = ({ orders }) => {
  if (!Array.isArray(orders)) return null;

  const hoursByDay = {
    Monday: Array(24).fill(0),
    Tuesday: Array(24).fill(0),
    Wednesday: Array(24).fill(0),
    Thursday: Array(24).fill(0),
    Friday: Array(24).fill(0),
    Saturday: Array(24).fill(0),
    Sunday: Array(24).fill(0),
  };

  const dayCounts = {
    Sunday: 0, Monday: 0, Tuesday: 0, Wednesday: 0,
    Thursday: 0, Friday: 0, Saturday: 0,
  };

  orders.forEach(order => {
    const date = dayjs(order.timestamp).tz("America/Toronto");
    const day = date.format("dddd");
    const hour = date.hour();
    if (hoursByDay[day]) {
      hoursByDay[day][hour]++;
      dayCounts[day]++;
    }
  });

  const weekdayDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const weekendDays = ["Saturday", "Sunday"];
  const hasWeekday = weekdayDays.some(day => dayCounts[day] > 0);
  const hasWeekend = weekendDays.some(day => dayCounts[day] > 0);

  const minHour = hasWeekday && hasWeekend ? 8 : hasWeekday ? 8 : 10;
  const maxHour = 17;

  const labels = Array.from({ length: maxHour - minHour + 1 }, (_, i) => `${i + minHour}:00`);


  const dayColors = {
    Monday: "#4dc9f6",
    Tuesday: "#36a2eb",
    Wednesday: "#007bff",
    Thursday: "#0056b3",
    Friday: "#003d80",
    Saturday: "#ffa726",
    Sunday: "#fb8c00",
  };

  const datasets = Object.entries(hoursByDay)
    .map(([day, hours]) => {
      const data = hours.slice(minHour, maxHour + 1);
      const total = data.reduce((a, b) => a + b, 0);
      if (total === 0) return null;
      return {
        label: day,
        data,
        backgroundColor: dayColors[day],
      };
    })
    .filter(Boolean);

  const hourlyData = {
    labels,
    datasets,
  };

  const orderedDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const transactionsPerDay = orderedDays.map(day => dayCounts[day]);

  const salesPerDay = orderedDays.map(day =>
    orders
      .filter(order => dayjs(order.timestamp).tz("America/Toronto").format("dddd") === day)
      .reduce((sum, order) => sum + parseFloat(order.grandTotal || 0), 0)
  );

  const dayData = {
    labels: orderedDays,
    datasets: [
      {
        label: "Transactions per Day",
        data: transactionsPerDay,
        backgroundColor: "rgba(255, 159, 64, 0.6)",
      },
    ],
  };

  const dayOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function () {
            return ""; // Skip default label
          },
          afterBody: function (context) {
            const index = context[0].dataIndex;
            const day = context[0].label;
            const transactions = transactionsPerDay[index];
            const total = salesPerDay[index];
            const average = transactions > 0 ? (total / transactions) : 0;

            return [
              `${day}: ${transactions} transactions – $${total.toFixed(2)}`,
              `Average: $${average.toFixed(2)}`
            ];
          },
        },
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  const hasFullWeek = Object.values(dayCounts).filter(Boolean).length >= 7;

  return (
    <div className="space-y-8 p-4">
      <div className="bg-white p-6 rounded shadow">
        <h2 className="text-xl font-semibold mb-4 text-center">
          Busy Hours
        </h2>
        <ChartWrapper>
          <Bar
            data={hourlyData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                tooltip: {
                  callbacks: {
                    label: () => '',
                    afterBody: function (context) {
                      const hourIndex = context[0].dataIndex;
                      const datasets = context[0].chart.data.datasets;

                      const sorted = datasets
                        .map(ds => ({
                          label: ds.label,
                          value: ds.data[hourIndex],
                          color: ds.backgroundColor,
                        }))
                        .filter(item => item.value > 0)
                        .sort((a, b) => b.value - a.value);

                      return sorted.map(item =>
                        `\u25A0 ${item.label}: ${item.value} transactions`
                      );
                    },
                  },
                  displayColors: true,
                },
              },
              interaction: { mode: "index", intersect: false },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { precision: 0 },
                },
              },
            }}
          />
        </ChartWrapper>
      </div>

      {hasFullWeek && (
        <div className="bg-white p-6 rounded shadow">
          <h2 className="text-xl font-semibold mb-4 text-center">
            Busy Days of the Week
          </h2>
          <ChartWrapper>
            <Bar
              data={dayData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  tooltip: dayOptions.plugins.tooltip,
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { precision: 0 },
                  },
                },
              }}
            />
          </ChartWrapper>
        </div>
      )}

    </div>
  );
};

export default BusyTimesView;
